import { useLocation, Link } from "@tanstack/react-router";
import { ChevronRight, type LucideIcon } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function NavMain({
  items,
}: {
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
}) {
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) => {
          // Check if any sub-item is active (exact match or starts with for dynamic routes like /dashboard/todo/[categoryId])
          const hasActiveSubItem = item.items?.some(subItem => {
            // Exact match
            if (pathname === subItem.url) return true;

            // Dynamic route: only match if it's a dynamic segment (not another static route)
            // For example: /dashboard/todo/[categoryId] should match /dashboard/todo but not /dashboard/todo/categories
            const otherSubUrls = item.items?.map(s => s.url).filter(u => u !== subItem.url) || [];
            const isOtherSubUrl = otherSubUrls.some(url => pathname === url || pathname.startsWith(url + '/'));

            if (isOtherSubUrl) return false;

            return pathname.startsWith(subItem.url + '/');
          });
          const isCurrentlyActive = pathname === item.url || hasActiveSubItem;

          // If item has sub-items
          if (item.items && item.items.length > 0) {
            // In collapsed mode, use dropdown menu
            if (isCollapsed) {
              return (
                <SidebarMenuItem key={item.title}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton tooltip={item.title}>
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="start" className="w-48">
                      {item.items.map((subItem) => {
                        const isSubItemActive = (() => {
                          if (pathname === subItem.url) return true;
                          const otherSubUrls = item.items?.map(s => s.url).filter(u => u !== subItem.url) || [];
                          const isOtherSubUrl = otherSubUrls.some(url => pathname === url || pathname.startsWith(url + '/'));
                          if (isOtherSubUrl) return false;
                          return pathname.startsWith(subItem.url + '/');
                        })();
                        return (
                          <DropdownMenuItem key={subItem.title} asChild>
                            <Link
                              to={subItem.url}
                              className={isSubItemActive ? "bg-accent" : ""}
                            >
                              {subItem.title}
                            </Link>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              );
            }

            // In expanded mode, use collapsible
            return (
              <Collapsible
                key={item.title}
                asChild
                defaultOpen={isCurrentlyActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={item.title}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => {
                        const isSubItemActive = (() => {
                          if (pathname === subItem.url) return true;
                          const otherSubUrls = item.items?.map(s => s.url).filter(u => u !== subItem.url) || [];
                          const isOtherSubUrl = otherSubUrls.some(url => pathname === url || pathname.startsWith(url + '/'));
                          if (isOtherSubUrl) return false;
                          return pathname.startsWith(subItem.url + '/');
                        })();
                        return (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton asChild isActive={isSubItemActive}>
                              <Link to={subItem.url}>
                                <span>{subItem.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            );
          }

          // Otherwise, render simple link
          const isItemActive = pathname === item.url;
          return (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton tooltip={item.title} asChild isActive={isItemActive}>
                <Link to={item.url}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
